import React from 'react';
import { Helmet } from 'react-helmet';
import Button from '../../components/ui/Button';
import { Activity, CheckCircle } from 'lucide-react';
export default function MentalCapacity() {
  return <main className="min-h-screen bg-white" data-id="element-1079">
      <Helmet data-id="element-1080">
        <title data-id="element-1081">Mental Capacity Assessments UK | Medico-Legal Psychiatry</title>
        <meta name="description" content="Expert Mental Capacity Assessments UK by Dr. Imtiaz Syed. Impartial COP3, DoLS, testamentary & litigation capacity reports for courts and legal proceedings." data-id="element-1082" />
        <meta name="keywords" content="Mental Capacity Assessments UK, Mental Capacity Act 2005, Capacity to Litigate, Testamentary Capacity, DoLS, COP3, Psychiatric capacity reports for COP3 court applications, Medico-Legal Psychiatry" data-id="element-1083" />
        <script type="application/ld+json" data-id="element-1084">
          {`
            {
              "@context": "https://schema.org",
              "@type": "MedicalService",
              "name": "Mental Capacity Assessments UK",
              "provider": {
                "@type": "Person",
                "name": "Dr. Imtiaz Syed",
                "jobTitle": "Psychiatrist & Expert Witness"
              },
              "serviceType": "Mental Capacity Assessment",
              "audience": {
                "@type": "Audience",
                "name": "Legal Professionals"
              },
              "availableChannel": {
                "@type": "ServiceChannel",
                "name": "Online and In-person",
                "serviceUrl": "https://medicolegal-psychiatry.co.uk/contact",
                "servicePhone": "",
                "serviceSmsNumber": ""
              },
              "serviceOutput": "Expert Mental Capacity Reports for Legal Proceedings"
            }
          `}
        </script>
      </Helmet>
      {/* Hero Section */}
      <section className="bg-primary text-white py-10 pt-24 md:pt-28" data-id="element-1085">
        <div className="container mx-auto px-4" data-id="element-1086">
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-serif font-bold mb-4 text-center" data-id="element-1087">
            Mental Capacity Assessments UK
          </h1>
          <p className="text-xl max-w-3xl mx-auto text-center" data-id="element-1088">
            Essential Legal Clarity & Protection
          </p>
        </div>
      </section>
      {/* Main Content */}
      <section className="py-12" data-id="element-1089">
        <div className="container mx-auto px-4" data-id="element-1090">
          <div className="max-w-3xl mx-auto" data-id="element-1091">
            <p className="text-slate-dark text-lg mb-6" data-id="element-1092">
              In complex legal matters, a clear understanding of an individual's
              mental capacity is paramount. Dr. Imtiaz Syed provides meticulous,
              impartial, and expert Mental Capacity Assessments, strictly
              adhering to the Mental Capacity Act 2005. Our goal is to equip
              legal professionals with robust evidence, ensuring decisions align
              with an individual's best interests and uphold their rights.
            </p>
            <h2 className="text-2xl font-serif font-bold text-primary mb-4" data-id="element-1093">
              Why Instruct Dr. Imtiaz Syed for Mental Capacity Assessments?
            </h2>
            <p className="text-slate-dark mb-4" data-id="element-1094">
              Dr. Syed brings deep clinical expertise and legal acumen to every
              capacity assessment, ensuring:
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8" data-id="element-1095">
              <div className="bg-gray-50 p-4 rounded-lg" data-id="element-1096">
                <div className="flex items-start" data-id="element-1097">
                  <CheckCircle className="h-5 w-5 text-secondary mr-2 mt-1" data-id="element-1098" />
                  <div data-id="element-1099">
                    <h3 className="font-bold text-primary" data-id="element-1100">Legal Compliance</h3>
                    <p className="text-slate" data-id="element-1101">
                      All assessments strictly follow the Mental Capacity Act
                      2005 and relevant case law.
                    </p>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 p-4 rounded-lg" data-id="element-1102">
                <div className="flex items-start" data-id="element-1103">
                  <CheckCircle className="h-5 w-5 text-secondary mr-2 mt-1" data-id="element-1104" />
                  <div data-id="element-1105">
                    <h3 className="font-bold text-primary" data-id="element-1106">
                      Impartial & Objective Insights
                    </h3>
                    <p className="text-slate" data-id="element-1107">
                      Providing unbiased, evidence-based opinions you can trust.
                    </p>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 p-4 rounded-lg" data-id="element-1108">
                <div className="flex items-start" data-id="element-1109">
                  <CheckCircle className="h-5 w-5 text-secondary mr-2 mt-1" data-id="element-1110" />
                  <div data-id="element-1111">
                    <h3 className="font-bold text-primary" data-id="element-1112">
                      Thorough & Sensitive Approach
                    </h3>
                    <p className="text-slate" data-id="element-1113">
                      Assessments are comprehensive, conducted with the utmost
                      respect and empathy for the individual.
                    </p>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 p-4 rounded-lg" data-id="element-1114">
                <div className="flex items-start" data-id="element-1115">
                  <CheckCircle className="h-5 w-5 text-secondary mr-2 mt-1" data-id="element-1116" />
                  <div data-id="element-1117">
                    <h3 className="font-bold text-primary" data-id="element-1118">
                      Courtroom-Ready Reports
                    </h3>
                    <p className="text-slate" data-id="element-1119">
                      Clear, concise, and robust reports designed to withstand
                      scrutiny.
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <h2 className="text-2xl font-serif font-bold text-primary mb-4" data-id="element-1120">
              Key Mental Capacity Assessments Provided
            </h2>
            <p className="text-slate-dark mb-4" data-id="element-1121">
              Dr. Syed offers expert evaluations across critical areas,
              including:
            </p>
            <ul className="space-y-3 mb-8 text-slate-dark" data-id="element-1122">
              <li className="flex items-start" data-id="element-1123">
                <span className="text-secondary font-bold mr-2" data-id="element-1124">•</span>
                <span data-id="element-1125">
                  <strong data-id="element-1126">Capacity to Litigate:</strong> Assessing the ability
                  to understand legal proceedings and instruct legal counsel.
                </span>
              </li>
              <li className="flex items-start" data-id="element-1127">
                <span className="text-secondary font-bold mr-2" data-id="element-1128">•</span>
                <span data-id="element-1129">
                  <strong data-id="element-1130">Capacity for Residence & Care Decisions:</strong>{' '}
                  Evaluating the ability to choose living arrangements and care.
                </span>
              </li>
              <li className="flex items-start" data-id="element-1131">
                <span className="text-secondary font-bold mr-2" data-id="element-1132">•</span>
                <span data-id="element-1133">
                  <strong data-id="element-1134">Capacity to Manage Property & Affairs (COP3):</strong>{' '}
                  Essential for financial decisions and Court of Protection
                  applications.
                </span>
              </li>
              <li className="flex items-start" data-id="element-1135">
                <span className="text-secondary font-bold mr-2" data-id="element-1136">•</span>
                <span data-id="element-1137">
                  <strong data-id="element-1138">Testamentary Capacity:</strong> Determining mental
                  capacity for making or changing a will.
                </span>
              </li>
              <li className="flex items-start" data-id="element-1139">
                <span className="text-secondary font-bold mr-2" data-id="element-1140">•</span>
                <span data-id="element-1141">
                  <strong data-id="element-1142">Capacity for Medical Treatment Decisions:</strong>{' '}
                  Assessing the ability to consent to or refuse medical care.
                </span>
              </li>
              <li className="flex items-start" data-id="element-1143">
                <span className="text-secondary font-bold mr-2" data-id="element-1144">•</span>
                <span data-id="element-1145">
                  <strong data-id="element-1146">
                    Deprivation of Liberty Safeguards (DoLS) & Section 21A
                    Challenges:
                  </strong>{' '}
                  Expert assessments for care arrangements where liberty may be
                  restricted.
                </span>
              </li>
            </ul>
            <h2 className="text-2xl font-serif font-bold text-primary mb-4" data-id="element-1147">
              Our Seamless Process & Robust Reports
            </h2>
            <p className="text-slate-dark mb-6" data-id="element-1148">
              Our assessment process is thorough and efficient, involving
              careful review of documentation and sensitive clinical interviews.
              Dr. Syed is flexible, offering both secure remote assessments
              across the UK and in-person consultations in specific regions
              (West Midlands, East Midlands, Greater London, or by arrangement).
              The resulting reports are always clear, legally relevant, and
              provide precise findings and actionable insights for your case.
            </p>
            <h2 className="text-2xl font-serif font-bold text-primary mb-4" data-id="element-1149">
              Partnering for Just Outcomes
            </h2>
            <p className="text-slate-dark mb-6" data-id="element-1150">
              Dr. Imtiaz Syed is your trusted partner for navigating mental
              capacity complexities. Rely on his expertise to provide the
              clarity needed for informed legal decisions and to ensure
              individual rights are always protected.
            </p>
            <div className="bg-gray-50 p-6 rounded-lg shadow-md mb-8" data-id="element-1151">
              <h3 className="text-xl font-serif font-bold text-primary mb-4" data-id="element-1152">
                Ready to Instruct Dr. Syed?
              </h3>
              <p className="text-slate-dark mb-4" data-id="element-1153">
                We invite you to discuss your specific case requirements.
              </p>
              <div className="flex items-center" data-id="element-1154">
                <span className="mr-2 text-slate-dark" data-id="element-1155">Contact Us:</span>
                <a href="mailto:enquiries@medicolegal-psychiatry.co.uk" className="text-primary hover:text-secondary transition-colors" data-id="element-1156">
                  enquiries@medicolegal-psychiatry.co.uk
                </a>
              </div>
            </div>
            <div className="text-center" data-id="element-1157">
              <Button variant="primary" size="lg" href="/contact" data-id="element-1158">
                Book a Mental Capacity Assessment
              </Button>
            </div>
          </div>
        </div>
      </section>
    </main>;
}